/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { TradeRuleShow } from 'src/components/TradeRule';
import TradeRuleShowPlaceholder from 'src/components/TradeRule/TradeRuleShow/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getTradeRuleShowRetryVariables from './helpers/getTradeRuleShowRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'trade_rule.trade_rule_show.initial_load_error.message',
    defaultMessage: 'An error occurred while loading this trade rule. Please try again.',
  },
});

export const tradeRuleShowRouteQuery = graphql`
  query TradeRuleShowRoute_Query($uuid: ID!, $ruleId: ID!) {
    property(uuid: $uuid) {
      ...TradeRuleShow_property
    }
    rule(uuid: $ruleId) {
      ...TradeRuleShow_rule
    }
  }
`;

const tradeRuleShowRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string,
      ruleId: PropTypes.string,
    }),
  }),
});

/**
 * Renders `TradeRuleShow` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} trade rule show route content.
 */
function TradeRuleShowPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(tradeRuleShowRouteQuery, queryRef);
  return <TradeRuleShow {...routeProps} {...queryData} />;
}

TradeRuleShowPreloadedQueryRenderer.propTypes = {
  routeProps: tradeRuleShowRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `TradeRuleShow` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} trade rule show route content.
 */
function TradeRuleShowRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeRuleShowRouteQuery,
    routeProps,
    {
      logLabel: 'TradeRuleShowRoute',
      getRetryVariablesFunc: getTradeRuleShowRetryVariables,
    },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={TradeRuleShowPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TradeRuleShowPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <TradeRuleShow {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeRuleShowRouteRenderer.propTypes = {
  routeProps: tradeRuleShowRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TradeRuleShow route state supplied by found-relay.
 * Network errors are handled before pending props so the local error boundary mounts.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TradeRuleShow route content or loading state.
 */
export function tradeRuleShowRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TradeRuleShowRouteRenderer
        key={`${routeProps.match.params.uuid}-${routeProps.match.params.ruleId}`}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <TradeRuleShowRouteRenderer
      key={`${props.match.params.uuid}-${props.match.params.ruleId}`}
      routeProps={props}
    />
  );
}

export default TradeRuleShowRouteRenderer;
